import {
  Box,
  Button,
  Chip,
  Grid,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { startOfDay } from "date-fns";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormCombos } from "../../../../hooks/useFormCombos";
import { getAllCommitteesByLanguageId } from "../../../../services/committees.service";
import { getCompaniesByUser } from "../../../../services/companies.service";
import { getMembersByName } from "../../../../services/formCombos.service.js";
import { getInputDateFormats } from "../../../../utils/get-date-time-locale";
import { Select } from "../../../Common/Select";
import Textarea from "../../../Common/Textarea";
import {
  actionsStatusOptions,
  getChairPersonsValidations,
} from "../../utils/index";
import {
  ButtonsContainer,
  Content,
  FieldsContainer,
  HeaderContainer,
  OpenedInputDescription,
} from "./styles";

const useStyles = makeStyles((theme) => ({
  button: {
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  buttonOutlined: {
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  chip: {
    margin: 2,
  },
}));

function FilterDrawer({ onClose, t, filters, setFilters, actionShowSpinner }) {
  const classes = useStyles();
  const { i18n } = useTranslation();

  const [committeesOptions, setCommitteesOptions] = useState([]);

  const [requesterInputValue, setRequesterInputValue] = useState("");
  const [requesters, setRequesters] = useState([]);
  const [selectedRequester, setSelectedRequester] = useState(filters.requester);
  const [responsibleInputValue, setResponsibleInputValue] = useState("");
  const [selectedCompanies, setSelectedCompanies] = useState(filters.companies);
  const [responsibles, setResponsibles] = useState([]);
  const [selectedResponsible, setSelectedResponsible] = useState(
    filters.selectedResponsible
  );
  const [focalPointInputValue, setFocalPointInputValue] = useState("");
  const [focalPoints, setFocalPoints] = useState([]);
  const [selectedFocalPoint, setSelectedFocalPoint] = useState(
    filters.focalPoint
  );
  const [directory, setDirectory] = useState(filters.directorship);
  const [actionStatus, setActionStatus] = useState([]);
  const [committee, setCommittee] = useState(filters.committee);
  const [slotType, setSlotType] = useState(filters.kind);
  const [actionNumber, setActionNumber] = useState(filters.actionNumber);
  const [chairpersonValidation, setChairpersonValidation] = useState(
    filters.chairpersonValidation
  );
  const [meetStartDate, setMeetStartDate] = useState(filters.meetStartDate);
  const [meetEndDate, setMeetEndDate] = useState(filters.meetEndDate);
  const [deadlineStartDate, setDeadlineStartDate] = useState(
    filters.deadlineStartDate
  );
  const [deadlineEndDate, setDeadlineEndDate] = useState(
    filters.deadlineEndDate
  );
  const [openedField, setOpenedField] = useState(filters.keyWord);
  const [actionOptions, setActionsOptions] = useState([]);
  const [companiesOptions, setCompaniesOptions] = useState([]);

  const {
    getDirectors,
    directorsList,
    sanitizeDirectorsForSelect,
    getTypeSlot,
    typeSlot,
  } = useFormCombos();

  useEffect(() => {
    const language = localStorage.getItem("i18nextLng");

    const loadList = async () => {
      const response = await getAllCommitteesByLanguageId(language);

      setCommitteesOptions(
        response.data.filter((committee) => committee.status !== "Inativo")
      );
    };

    const loadCompaniesOptions = async () => {
      const companies = await getCompaniesByUser();

      setCompaniesOptions(companies);
    };

    actionShowSpinner(true);

    loadCompaniesOptions();
    loadList();

    getDirectors(language);
    getTypeSlot(language);

    actionShowSpinner(false);
  }, []);

  useEffect(() => {
    const newActionOptions =
      actionsStatusOptions(localStorage.getItem("i18nextLng")) || [];

    setActionsOptions(newActionOptions);

    setSelectedCompanies(filters?.companies);
    setSelectedResponsible(filters?.responsible);
    setSelectedRequester(filters?.requester);
    setDirectory(filters?.directorship);
    setSlotType(filters?.kind);
    setCommittee(filters?.committee);
    setMeetStartDate(filters?.meetStartDate);
    setMeetEndDate(filters?.meetEndDate);
    setActionStatus(filters?.actionStatus || []);
    setActionNumber(filters?.actionNumber);
    setChairpersonValidation(filters?.chairpersonValidation);
    setDeadlineStartDate(filters?.deadlineStartDate);
    setDeadlineEndDate(filters?.deadlineEndDate);
    setOpenedField(filters?.keyWord);
    setSelectedFocalPoint(filters?.focalPoint);
  }, [filters]);

  useEffect(() => {
    async function load() {
      if (requesterInputValue?.length >= 3) {
        const res = await getMembersByName("pt", requesterInputValue);

        if (res.error?.length > 0) {
          setRequesters([]);
        }

        setRequesters(res.data);
      } else {
        setRequesters([]);
      }

      if (responsibleInputValue?.length >= 3) {
        const res = await getMembersByName("pt", responsibleInputValue);

        if (res.error?.length > 0) {
          setResponsibles([]);
        }

        setResponsibles(res.data);
      } else {
        setResponsibles([]);
      }

      if (focalPointInputValue?.length >= 3) {
        const res = await getMembersByName("pt", focalPointInputValue);

        if (res.error?.length > 0) {
          setFocalPoints([]);
        }

        setFocalPoints(res.data);
      } else {
        setFocalPoints([]);
      }
    }

    load();
  }, [requesterInputValue, focalPointInputValue, responsibleInputValue]);

  const handleApplyFilters = () => {
    setFilters({
      ...(actionStatus && { actionStatus }),
      ...(selectedCompanies && { companies: selectedCompanies }),
      ...(selectedResponsible && { responsible: selectedResponsible }),
      ...(selectedRequester && { requester: selectedRequester }),
      ...(directory && { directorship: directory }),
      ...(slotType && { kind: slotType }),
      ...(committee && { committee: committee }),
      ...(meetStartDate
        ? { meetStartDate: meetStartDate }
        : { meetStartDate: null }),
      ...(meetEndDate ? { meetEndDate: meetEndDate } : { meetEndDate: null }),
      ...(deadlineStartDate
        ? { deadlineStartDate: deadlineStartDate }
        : { deadlineStartDate: null }),
      ...(deadlineEndDate
        ? { deadlineEndDate: deadlineEndDate }
        : { deadlineEndDate: null }),
      ...(openedField && { keyWord: openedField }),
      ...(actionNumber && { actionNumber }),
      ...(chairpersonValidation && { chairpersonValidation }),
      ...(selectedFocalPoint && { focalPoint: selectedFocalPoint }),
    });

    onClose();
  };

  const handleCleanFilters = () => {
    setFilters({
      actionStatus: [],
      responsible: undefined,
      requester: undefined,
      directorship: undefined,
      kind: undefined,
      committee: undefined,
      meetStartDate: null,
      meetEndDate: null,
      deadlineStartDate: null,
      deadlineEndDate: null,
      keyWord: undefined,
      actionNumber: undefined,
      chairpersonValidation: undefined,
    });

    onClose();
  };

  return (
    <Box sx={{ width: 460, flex: 1, display: "flex" }} role="presentation">
      <Content>
        <HeaderContainer>
          <Typography variant="h4">{t("forms.filters")}</Typography>

          <Close
            onClick={() => {
              onClose();
            }}
          />
        </HeaderContainer>

        <FieldsContainer>
          <Grid container spacing={3}>
            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="requester"
                options={requesters}
                value={selectedRequester}
                onInputChange={(_event, newInputValue, _prop) => {
                  setRequesterInputValue(newInputValue);
                }}
                inputValue={requesterInputValue}
                label={t("forms.minutes.actions.requesterName")}
                setValue={(value) => {
                  setSelectedRequester(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="responsible"
                options={responsibles}
                value={selectedResponsible}
                onInputChange={(_event, newInputValue, _prop) => {
                  setResponsibleInputValue(newInputValue);
                }}
                inputValue={responsibleInputValue}
                label={t("menu.actions.filters.responsibles")}
                setValue={(value) => {
                  setSelectedResponsible(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="focalPoint"
                options={focalPoints}
                value={selectedFocalPoint}
                onInputChange={(_event, newInputValue, _prop) => {
                  setFocalPointInputValue(newInputValue);
                }}
                inputValue={focalPointInputValue}
                label={t("forms.minutes.actions.focalPoint")}
                setValue={(value) => {
                  setSelectedFocalPoint(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="directories"
                options={sanitizeDirectorsForSelect(directorsList)}
                value={directory}
                label={t("menu.actions.filters.responsiblesDirectory")}
                setValue={(value) => {
                  setDirectory(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                id="companies"
                options={
                  companiesOptions?.map((item) => ({
                    id: item.companyId,
                    name: `${item.companyName} - ${item.companyAbbreviation}`,
                  })) ?? []
                }
                getOptionSelected={(option, value) => option.id === value.id}
                value={selectedCompanies}
                label={t("slots.companies")}
                setValue={(values) => {
                  setSelectedCompanies(values);
                }}
                multiple={true}
                renderTags={(selectedValues) => {
                  return selectedValues.map((option) => (
                    <Chip
                      className={classes.chip}
                      key={option.id}
                      onDelete={() => {
                        setSelectedCompanies(
                          selectedCompanies.filter((v) => v.id !== option.id)
                        );
                      }}
                      onClick={() => {
                        setSelectedCompanies(
                          selectedCompanies.filter((v) => v.id !== option.id)
                        );
                      }}
                      variant="outlined"
                      label={option.name}
                    />
                  ));
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="status"
                options={actionOptions}
                value={actionStatus}
                label={t("menu.actions.status")}
                setValue={(values) => {
                  setActionStatus(values);
                }}
                multiple={true}
                renderTags={(selectedValues) => {
                  return selectedValues.map((option) => (
                    <Chip
                      className={classes.chip}
                      key={option.id}
                      onDelete={() => {
                        setActionStatus(
                          actionStatus.filter((v) => v.id !== option.id)
                        );
                      }}
                      onClick={() => {
                        setActionStatus(
                          actionStatus.filter((v) => v.id !== option.id)
                        );
                      }}
                      variant="outlined"
                      label={option.name}
                    />
                  ));
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                id="committee"
                options={committeesOptions}
                value={committee}
                label={t("slots.committee")}
                setValue={(value) => {
                  setCommittee(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                id="type"
                options={typeSlot}
                value={slotType}
                label={t("slots.type")}
                setValue={(value) => {
                  setSlotType(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <TextField
                name="actionNumber"
                id="actionNumber"
                onChange={(e) => setActionNumber(e.target.value)}
                value={actionNumber}
                label={t("forms.minutes.actions.actionNumber")}
                style={{ width: "100%" }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                id="chairpersopnValidation"
                options={getChairPersonsValidations() || []}
                label={t("menu.actions.chairpersonValidation")}
                value={chairpersonValidation}
                setValue={(value) => {
                  setChairpersonValidation(value);
                }}
              />
            </Grid>

            <Grid item lg={6} xs={6}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="inline"
                format={getInputDateFormats(i18n.language)}
                id="createdIn"
                fullWidth
                label={t("menu.actions.meetStartDate")}
                value={meetStartDate}
                onChange={(date) =>
                  setMeetStartDate(date ? startOfDay(date) : null)
                }
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </Grid>

            <Grid item lg={6} xs={6}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="inline"
                format={getInputDateFormats(i18n.language)}
                id="createdIn"
                fullWidth
                label={t("menu.actions.meetEndDate")}
                value={meetEndDate}
                onChange={(date) =>
                  setMeetEndDate(date ? startOfDay(date) : null)
                }
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </Grid>

            <Grid item lg={6} xs={6}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="inline"
                format={getInputDateFormats(i18n.language)}
                id="createdIn"
                fullWidth
                label={t("menu.actions.deadlineStartDate")}
                value={deadlineStartDate}
                onChange={(date) =>
                  setDeadlineStartDate(date ? startOfDay(date) : null)
                }
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </Grid>

            <Grid item lg={6} xs={6}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="inline"
                format={getInputDateFormats(i18n.language)}
                id="createdIn"
                fullWidth
                label={t("menu.actions.deadlineEndDate")}
                value={deadlineEndDate}
                onChange={(date) =>
                  setDeadlineEndDate(date ? startOfDay(date) : null)
                }
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Textarea
                name="freeField"
                id="freeField"
                onChange={(e) => setOpenedField(e.target.value)}
                value={openedField}
                label={t("forms.openedField")}
                style={{ width: "100%" }}
              />

              <OpenedInputDescription>
                {t("forms.actionsFreeInput")}
              </OpenedInputDescription>
            </Grid>
          </Grid>
        </FieldsContainer>

        <ButtonsContainer>
          <Button
            variant="contained"
            onClick={handleCleanFilters}
            className={classes.buttonOutlined}
          >
            {t("forms.cleanFilters")}
          </Button>

          <Button
            variant="contained"
            onClick={handleApplyFilters}
            className={classes.button}
          >
            {t("forms.applyFilters")}
          </Button>
        </ButtonsContainer>
      </Content>
    </Box>
  );
}

export default FilterDrawer;
